import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, Lightning, Cpu, Target, Crown, Rocket, ArrowRight, ChartLineUp, Lock,
  Compass, Sparkle
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import DiscoverySession from "@/components/DiscoverySession";
import usePageMeta from "@/hooks/usePageMeta";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/b72a63e6-fc80-4ab9-91dc-1619fd51bce9/images/79ebc1c93832bc5a0bc1db9496f6f4e0bfc04e94cad4065b479d593ab13de4aa.png";

const PILLARS = [
  { icon: Lightning, title: "Zero-Cap Hull", desc: "Launch any business with near-zero capital using tactical commissioning blueprints." },
  { icon: Cpu, title: "Ghost Crew AI", desc: "Sector-specific AI agent fleets deployed for automation, lead-capture & CRM." },
  { icon: Target, title: "Sovereign Signal", desc: "Strategic marketing strikes — tailored campaigns for your specific market." },
];

export default function Home() {
  usePageMeta(
    "Sovereign Provisions",
    "Industrial-grade digital provisions for the Sovereign operator — 4 flagship products, 53 business starters, and an AI Blueprint Engine. Outclass Gumroad and LemonSqueezy."
  );
  const [flagships, setFlagships] = useState([]);
  const [starters, setStarters] = useState([]);

  useEffect(() => {
    api.get("/products?sector=flagship").then((r) => setFlagships(r.data.products));
    api.get("/products?sector=starter").then((r) => setStarters(r.data.products.slice(0, 8)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/40 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="tactical-line w-12" />
              <span className="overline">// Sovereign Provisions // V.2026</span>
            </div>

            <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-8 leading-[0.95]" data-testid="hero-title">
              Industrial-grade<br />
              <span className="text-[#FFD700] text-glow-gold">digital provisions</span><br />
              for the Sovereign operator.
            </h1>

            <p className="text-base md:text-lg text-white/70 font-light max-w-2xl mb-10 leading-relaxed">
              EIGOO Provisions is the central armory packaging elite operational intelligence into
              industrial-grade digital products. Outclassing Gumroad. Outranking LemonSqueezy.
              Built for the operator class.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/catalog"
                data-testid="hero-cta-arsenal"
                className="btn-gold px-8 py-4 text-sm flex items-center gap-3"
              >
                Enter The Arsenal
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/blueprint"
                data-testid="hero-cta-blueprint"
                className="btn-ghost px-8 py-4 text-sm flex items-center gap-3"
              >
                <Sparkle size={18} weight="duotone" />
                Generate My Blueprint
              </Link>
            </div>

            <div className="mt-16 flex items-center gap-10 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/50">
              <div><span className="text-[#FFD700] text-xl font-bold block">53+</span>Business Sectors</div>
              <div className="w-px h-8 bg-white/10" />
              <div><span className="text-[#FFD700] text-xl font-bold block">4</span>Flagship Products</div>
              <div className="w-px h-8 bg-white/10" />
              <div><span className="text-[#FFD700] text-xl font-bold block">∞</span>AI Blueprints</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS — THE EIGOO WAY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32" data-testid="pillars-section">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <span className="overline">// The Doctrine</span>
            <h2 className="heading-display text-4xl md:text-5xl mt-4">
              Every provision<br />follows <span className="text-[#FFD700]">The EIGOO Way.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="surface-card p-6 lg:p-8"
              >
                <div className="w-12 h-12 border border-[#FFD700]/40 flex items-center justify-center mb-6">
                  <p.icon size={22} weight="duotone" className="text-[#FFD700]" />
                </div>
                <div className="overline text-[0.6rem] mb-2">Pillar {i + 1}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{p.title}</h3>
                <p className="text-sm text-white/60 font-light leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DISCOVERY SESSION CTA */}
      <DiscoverySession />

      {/* FLAGSHIP ARSENAL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="flagship-section">        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="overline">// The Flagship Arsenal</span>
            <h2 className="heading-display text-4xl md:text-5xl mt-4">Capital Ships.</h2>
          </div>
          <Link to="/catalog?sector=flagship" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFD700] flex items-center gap-2 hover:gap-3 transition-all">
            View Full Fleet <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {flagships.map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      </section>

      {/* BLUEPRINT GENERATOR CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="blueprint-cta-section">
        <div className="surface-card armor-border p-8 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 tactical-grid opacity-30" />
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#FFD700]/10 blur-3xl" />
          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="overline mb-4 block">// Universal Engine</span>
              <h2 className="heading-display text-4xl md:text-5xl mb-6">
                The Custom <span className="text-[#FFD700]">Blueprint Generator</span>
              </h2>
              <p className="text-white/70 font-light leading-relaxed mb-8">
                Input <em>any</em> business type. The Sovereign Director walks you through three Command Stages:
                Hull Commissioning, Exoskeleton Outfitting, and Signal Ignition. Interactive checklists.
                Director&apos;s voice. Industrial-grade execution.
              </p>
              <Link to="/blueprint" data-testid="blueprint-cta-btn" className="btn-gold px-8 py-4 text-sm inline-flex items-center gap-3">
                <Compass size={18} weight="duotone" />
                Commission My Blueprint
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {[
                { num: "01", title: "Hull Commissioning", desc: "Legal & entity setup, sector-specific" },
                { num: "02", title: "Exoskeleton Outfitting", desc: "AI Ghost Crew deployment" },
                { num: "03", title: "Signal Ignition", desc: "First strategic marketing strike" },
              ].map((s) => (
                <div key={s.num} className="flex items-start gap-5 p-4 border-l-2 border-[#FFD700]/30 hover:border-[#FFD700] transition-colors">
                  <div className="font-mono text-3xl font-bold text-[#FFD700]/60">{s.num}</div>
                  <div>
                    <div className="font-bold uppercase tracking-wider text-sm text-white">{s.title}</div>
                    <div className="text-xs text-white/50 font-light mt-1">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STARTERS PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="starters-section">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="overline">// 50+ Sovereign Business Starters</span>
            <h2 className="heading-display text-4xl md:text-5xl mt-4">Every sector. Armored.</h2>
          </div>
          <Link to="/catalog?sector=starter" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFD700] flex items-center gap-2 hover:gap-3 transition-all">
            Browse All Starters <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {starters.map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      </section>

      {/* CLOSING SOVEREIGN STATEMENT */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="closing-section">
        <Crown size={40} weight="duotone" className="text-[#FFD700] mx-auto mb-6" />
        <h2 className="heading-display text-4xl md:text-6xl mb-6">
          Provision yourself.<br />
          <span className="text-[#FFD700]">Then dominate.</span>
        </h2>
        <p className="text-white/60 font-light max-w-2xl mx-auto mb-8">
          The master library for the Sovereign individual. Every blueprint thorough, high-fidelity,
          and armored. Built to outclass every marketplace in the industry.
        </p>
        <Link to="/register" data-testid="closing-cta" className="btn-gold px-8 py-4 text-sm inline-flex items-center gap-3">
          Commission Your Account
          <Rocket size={18} weight="duotone" />
        </Link>
      </section>
    </div>
  );
}
